import React, { useEffect, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { getColumnDefinitionByType } from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Avatar } from 'uiToolkitV1/Avatar';
import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium, TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

import { NumericPercentageInput } from './shared/NumericPercentageInput';
import { StakeholderErrorState, StakeholderEmptyState } from './shared/stakeholderComponents';
import { MOCK_CREATE_GUIDELINE_COLUMNS, MOCK_CREATE_GUIDELINE_ROW_DATA } from './shared/stakeholderConstants';
// Shared imports
import { StakeHolderTableProps } from './shared/stakeholderTypes';
import {
  setModifiedCreateGuidelineRows,
  setOriginalCreateGuidelineRows,
  useCompensationDrawersState,
} from './slice/compensationDrawersSlice';

function findClosestParentRow(row, allRows) {
  if (!row.hierarchy || row.hierarchy.length < 2) return null;
  // Remove the last element to get the parent hierarchy
  const parentHierarchy = row.hierarchy.slice(0, -1);
  return allRows.find(
    (r) =>
      Array.isArray(r.hierarchy) &&
      r.hierarchy.length === parentHierarchy.length &&
      r.hierarchy.every((val, idx) => val === parentHierarchy[idx])
  );
}

function isPerformanceSame(perf1, perf2) {
  return JSON.stringify(perf1) === JSON.stringify(perf2);
}

function findFirstMatchingAncestor(currentRow, allRows) {
  if (!currentRow.hierarchy || currentRow.hierarchy.length < 2) return null;

  let currentParent = findClosestParentRow(currentRow, allRows);

  // Keep checking parents until we find a match or reach the top level
  while (currentParent) {
    if (isPerformanceSame(currentRow.performance, currentParent.performance)) {
      return currentParent;
    }

    // Move up to the next parent level
    currentParent = findClosestParentRow(currentParent, allRows);
  }

  return null; // No matching ancestor found
}

const EditRowButton = (props) => {
  const { isInEditMode, api, onChange = () => {} } = props ?? {};

  const handleEdit = () => {
    onChange();
    setTimeout(() => {
      if (api?.resetRowHeights && api?.redrawRows) {
        api.resetRowHeights();
        api.redrawRows();
      }
    }, 100);
  };

  return (
    <Button
      variant="tertiary"
      onClick={() => {
        handleEdit();
      }}
      marginRight={isInEditMode ? '0px' : '12px'}
    >
      {isInEditMode ? 'Done' : 'Edit'}
    </Button>
  );
};

const EditMinMaxValue = ({ params }: { params: any }) => {
  const { data, api } = params;
  const { isInEditMode, id, performance } = data;
  const [localPerformance, setLocalPerformance] = useState(performance);

  const dispatch = useDispatch();
  const { modifiedCreateGuidelineRows } = useCompensationDrawersState();

  // Find the first matching ancestor by recursively checking parents
  const matchingAncestor = findFirstMatchingAncestor(data, modifiedCreateGuidelineRows);
  const isSameAsAncestor = matchingAncestor && isPerformanceSame(performance, matchingAncestor.performance);

  const { needsImprovement, meetsExpectations, exceedsExpectations, outstanding } = localPerformance ?? {};
  const { minValue: minValueNeedsImprovement, maxValue: maxValueNeedsImprovement } = needsImprovement ?? {};
  const { minValue: minValueMeetsExpectations, maxValue: maxValueMeetsExpectations } = meetsExpectations ?? {};
  const { minValue: minValueExceedsExpectations, maxValue: maxValueExceedsExpectations } = exceedsExpectations ?? {};
  const { minValue: minValueOutstanding, maxValue: maxValueOutstanding } = outstanding ?? {};

  const handleChange = () => {
    const newRows = modifiedCreateGuidelineRows.map((row) => {
      if (row.id === id) {
        return { ...row, performance: localPerformance, isInEditMode: !row.isInEditMode };
      }
      return row;
    });
    dispatch(setModifiedCreateGuidelineRows(newRows));
  };

  if (isInEditMode) {
    return (
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
        width="100%"
        height="100%"
      >
        <Box
          display="flex"
          padding="12px 12px 12px 12px"
          flexDirection="column"
          alignItems="flex-start"
          gap="0px"
          flexShrink="0"
          w="100%"
        >
          <Box display="flex" alignItems="flex-start" gap="0px" alignSelf="stretch" flexDirection="column" w="100%">
            <Flex justifyContent="space-between" alignItems="center" w="100%">
              <TextLabel bold>Editing guidelines</TextLabel>
              <EditRowButton isInEditMode={isInEditMode} id={id} api={api} onChange={handleChange} />
            </Flex>
            <Flex flexDir="column" bg="white" borderRadius="8px" border="1px solid #F5F7F9" w="100%">
              <Box display="flex" flexDirection="row" alignItems="flex-start" alignSelf="stretch">
                <Box display="flex" alignItems="center" alignSelf="stretch" padding="8px 12px" flex={1}>
                  <TextBodyMedium>Needs improvement</TextBodyMedium>
                </Box>
                <Box display="flex" alignItems="center" alignSelf="stretch" padding="8px 12px">
                  <NumericPercentageInput
                    value={minValueNeedsImprovement}
                    isDashed
                    showPercentage
                    onChange={(value) => {
                      const newPerformance = {
                        ...localPerformance,
                        needsImprovement: { ...needsImprovement, minValue: value },
                      };
                      setLocalPerformance(newPerformance);
                    }}
                  />
                </Box>
                <Box display="flex" alignItems="center" alignSelf="stretch" padding="8px 12px">
                  <NumericPercentageInput
                    value={maxValueNeedsImprovement}
                    showPercentage
                    onChange={(value) => {
                      const newPerformance = {
                        ...localPerformance,
                        needsImprovement: { ...needsImprovement, maxValue: value },
                      };
                      setLocalPerformance(newPerformance);
                    }}
                  />
                </Box>
              </Box>
              <Box display="flex" flexDirection="row" alignItems="flex-start" alignSelf="stretch">
                <Box display="flex" alignItems="center" alignSelf="stretch" padding="8px 12px" flex={1}>
                  <TextBodyMedium>Meets expectations</TextBodyMedium>
                </Box>
                <Box display="flex" alignItems="center" alignSelf="stretch" padding="8px 12px">
                  <NumericPercentageInput
                    value={minValueMeetsExpectations}
                    showPercentage
                    onChange={(value) => {
                      const newPerformance = {
                        ...localPerformance,
                        meetsExpectations: { ...meetsExpectations, minValue: value },
                      };
                      setLocalPerformance(newPerformance);
                    }}
                  />
                </Box>
                <Box display="flex" alignItems="center" alignSelf="stretch" padding="8px 12px">
                  <NumericPercentageInput
                    value={maxValueMeetsExpectations}
                    showPercentage
                    onChange={(value) => {
                      const newPerformance = {
                        ...localPerformance,
                        meetsExpectations: { ...meetsExpectations, maxValue: value },
                      };
                      setLocalPerformance(newPerformance);
                    }}
                  />
                </Box>
              </Box>
              <Box display="flex" flexDirection="row" alignItems="flex-start" alignSelf="stretch">
                <Box display="flex" alignItems="center" alignSelf="stretch" padding="8px 12px" flex={1}>
                  <TextBodyMedium>Exceeds expectations</TextBodyMedium>
                </Box>
                <Box display="flex" alignItems="center" alignSelf="stretch" padding="8px 12px">
                  <NumericPercentageInput
                    value={minValueExceedsExpectations}
                    showPercentage
                    onChange={(value) => {
                      const newPerformance = {
                        ...localPerformance,
                        exceedsExpectations: { ...exceedsExpectations, minValue: value },
                      };
                      setLocalPerformance(newPerformance);
                    }}
                  />
                </Box>
                <Box display="flex" alignItems="center" alignSelf="stretch" padding="8px 12px">
                  <NumericPercentageInput
                    value={maxValueExceedsExpectations}
                    showPercentage
                    onChange={(value) => {
                      const newPerformance = {
                        ...localPerformance,
                        exceedsExpectations: { ...exceedsExpectations, maxValue: value },
                      };
                      setLocalPerformance(newPerformance);
                    }}
                  />
                </Box>
              </Box>
              <Box display="flex" flexDirection="row" alignItems="flex-start" alignSelf="stretch">
                <Box display="flex" alignItems="center" alignSelf="stretch" padding="8px 12px" flex={1}>
                  <TextBodyMedium>Outstanding</TextBodyMedium>
                </Box>
                <Box display="flex" alignItems="center" alignSelf="stretch" padding="8px 12px">
                  <NumericPercentageInput
                    value={minValueOutstanding}
                    showPercentage
                    onChange={(value) => {
                      const newPerformance = {
                        ...localPerformance,
                        outstanding: { ...outstanding, minValue: value },
                      };
                      setLocalPerformance(newPerformance);
                    }}
                  />
                </Box>
                <Box display="flex" alignItems="center" alignSelf="stretch" padding="8px 12px">
                  <NumericPercentageInput
                    value={maxValueOutstanding}
                    showPercentage
                    onChange={(value) => {
                      const newPerformance = {
                        ...localPerformance,
                        outstanding: { ...outstanding, maxValue: value },
                      };
                      setLocalPerformance(newPerformance);
                    }}
                  />
                </Box>
              </Box>
            </Flex>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!isInEditMode && isSameAsAncestor) {
    // Get a meaningful label for the matching ancestor
    const ancestorLabel =
      matchingAncestor?.function?.level ||
      matchingAncestor?.function?.displayLevel ||
      matchingAncestor?.function?.displayName ||
      'ancestor';

    return (
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
        <Box display="flex" padding="12px" flexDirection="row" alignItems="flex-start" gap="0px" flexShrink="0">
          <TextBodyMedium>
            Same as {ancestorLabel?.toLowerCase() === 'bu' ? 'BU' : ancestorLabel?.toLowerCase()} guideline
          </TextBodyMedium>
        </Box>
        <EditRowButton isInEditMode={isInEditMode} id={id} api={api} onChange={handleChange} />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
      <Box
        display="flex"
        padding="12px"
        flexDirection="row"
        alignItems="flex-start"
        gap="4px"
        maxW="80%"
        flexWrap="wrap"
      >
        <TextBodyMedium>
          Needs Improvement: {minValueNeedsImprovement} - {maxValueNeedsImprovement}% •
        </TextBodyMedium>
        <TextBodyMedium>
          Meets Expectations: {minValueMeetsExpectations} - {maxValueMeetsExpectations}% •
        </TextBodyMedium>
        <TextBodyMedium>
          Exceeds Expectations: {minValueExceedsExpectations} - {maxValueExceedsExpectations}% •
        </TextBodyMedium>
        <TextBodyMedium>
          Outstanding: {minValueOutstanding} - {maxValueOutstanding}%
        </TextBodyMedium>
      </Box>
      <EditRowButton isInEditMode={isInEditMode} id={id} api={api} onChange={handleChange} />
    </Box>
  );
};

const customColumnPropsMap = {
  function: {
    flex: 1,
    autoHeight: true,
    metadata: { isEmployeeSubtitle: true },
    headerName: 'Function tree',
  },
  label: {
    colSpan: () => 3,
    width: undefined,
    cellRenderer: (params) => <EditMinMaxValue params={params} />,
  },
};

const HierarchyLevelCell = ({ data }: { data: any }) => {
  const { function: functionData } = data;
  const { displayName, subtitle, isEmployee, isGroup, profilePhoto, designation, level, displayLevel } = functionData;

  if (isEmployee || level === 'EMPLOYEE') {
    return (
      <Box display="flex" alignItems="center" py={1}>
        <Avatar name={displayName} src={profilePhoto} size={32} mr={3} />
        <Box>
          <TextBodyMedium>{displayName}</TextBodyMedium>
          <TextLabel>{subtitle}</TextLabel>
          {designation && <TextLabelSmall>{designation}</TextLabelSmall>}
        </Box>
      </Box>
    );
  }

  if (isGroup) {
    return (
      <Box display="flex" flexDirection="column" padding="8px 12px">
        <TextLabelSmall>{displayLevel}</TextLabelSmall>
        <TextBodyMedium>{displayName}</TextBodyMedium>
        {subtitle && <TextLabel>{subtitle}</TextLabel>}
      </Box>
    );
  }

  // Fallback for any other cases
  return <TextBodyMedium>{displayName}</TextBodyMedium>;
};

function CreateGuidelineTable({ gridRef, gridStatePersistenceKey }: StakeHolderTableProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentCompRef = useRef<any>(null);
  const agGridRef = gridRef ?? currentCompRef;
  const { modifiedCreateGuidelineRows } = useCompensationDrawersState();
  const [columnDefs, setColumnDefs] = useState([]);
  const dispatch = useDispatch();
  const columns = MOCK_CREATE_GUIDELINE_COLUMNS;
  const rows = MOCK_CREATE_GUIDELINE_ROW_DATA;
  const error = false;
  const isFetching = false;

  useEffect(() => {
    if (!columns || columns.length === 0) return;

    const newColumnDefs = columns.map((col) => ({
      colId: col.columnId,
      field: col.columnId,
      id: col.columnId,
      displayName: col.displayName,
      description: col.description,
      headerName: col.displayName,
      minWidth: 250,
      ...getColumnDefinitionByType(col),
      ...(customColumnPropsMap[col.columnId] || {}),
    }));

    setColumnDefs(newColumnDefs);
  }, [columns]);

  useEffect(() => {
    if (rows.length > 0) {
      const newRows = rows.map((row) => row);
      dispatch(setOriginalCreateGuidelineRows(newRows));
      dispatch(setModifiedCreateGuidelineRows(newRows));
    }
  }, [dispatch, rows]);

  // Error state handling , enable this when we have a real data
  if (error) {
    return (
      <StakeholderErrorState
        heading="No stake holders are available!"
        description="There was an issue fetching the stake holders. Please try again later."
      />
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start" borderRadius="8px" width="100%">
      {rows?.length === 0 && !isFetching ? (
        <StakeholderEmptyState heading="No stake holders are available!" />
      ) : (
        <Box className="stakeholder-flat-list-table" width="100%" height="calc(100vh - 265px)">
          <AgGridTable
            className="create-guideline-table"
            key={gridStatePersistenceKey}
            gridStatePersistenceKey={gridStatePersistenceKey}
            ref={agGridRef}
            columnDefs={columnDefs}
            paginationAutoPageSize={false}
            headerHeight={40}
            sideBar={null}
            rowData={modifiedCreateGuidelineRows}
            getRowId={(params) => params?.data?.id}
            treeData
            animateRows
            groupDefaultExpanded={-1}
            getDataPath={(data) => data?.hierarchy}
            autoGroupColumnDef={{
              headerName: 'Function tree',
              minWidth: 220,
              lockPosition: 'left',
              flex: 1,
              cellRendererParams: {
                suppressCount: true,
                innerRenderer: HierarchyLevelCell,
              },
              cellStyle: (params) => {
                const { isInEditMode } = params?.data ?? {};
                return isInEditMode
                  ? { display: 'flex', alignItems: 'center', paddingTop: '12px' }
                  : { height: '100%' };
              },
            }}
            kebabMenuIsRowSpecific={false}
            defaultColDef={{
              cellStyle: { display: 'flex', alignItems: 'center' },
              headerComponent: CustomHeaderCell,
            }}
            getRowHeight={(params) => {
              const { isInEditMode } = params?.data ?? {};
              return isInEditMode ? 290 : 66;
            }}
            getRowClass={(params) => {
              const { isInEditMode } = params?.data ?? {};
              return isInEditMode ? 'create-guideline-table-row-in-edit-mode' : '';
            }}
            showSidebarToggle={false}
          />
        </Box>
      )}
    </Box>
  );
}

export { CreateGuidelineTable };
